import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { DEFAULT_CONTENT } from "@/constants/siteDefaults";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = ["Glass Replacement", "Aluminium Windows & Doors", "Shower Screens", "Shopfronts", "Mirrors", "Splashbacks", "Other"];

const EMPTY = { name: "", email: "", phone: "", service: "", message: "" };

export default function Contact({ content }) {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const heading = content?.contact_heading || DEFAULT_CONTENT.contact_heading;

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry sent! We'll be in touch shortly.");
      setForm(EMPTY);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong — please call us instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-24 px-6 md:px-12 lg:px-24 bg-slate-50" data-testid="contact-section">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-16">
        <div className="lg:col-span-2">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-4">Get a quote</p>
          <h2 className="font-outfit text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-slate-800" data-testid="contact-heading">
            {heading}
          </h2>
          <div className="mt-10 space-y-6">
            <a href="tel:+61404179886" data-testid="contact-phone-link" className="flex items-center gap-4 group">
              <span className="border border-slate-300 p-3 group-hover:bg-slate-900 group-hover:text-white transition-colors duration-200"><Phone size={18} /></span>
              <span>
                <span className="block text-xs uppercase tracking-wider text-slate-500 font-bold">Call or text</span>
                <span className="font-semibold text-slate-900">0404 179 886</span>
              </span>
            </a>
            <a href="mailto:matthew@asapglassnsw.com.au" data-testid="contact-email-link" className="flex items-center gap-4 group">
              <span className="border border-slate-300 p-3 group-hover:bg-slate-900 group-hover:text-white transition-colors duration-200"><Mail size={18} /></span>
              <span>
                <span className="block text-xs uppercase tracking-wider text-slate-500 font-bold">Email</span>
                <span className="font-semibold text-slate-900">matthew@asapglassnsw.com.au</span>
              </span>
            </a>
            <a
              href="https://wa.me/61404179886?text=Hi%20ASAP%20Glass%2C%20I%27d%20like%20a%20quote"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-link"
              className="flex items-center gap-4 group"
            >
              <span className="border border-slate-300 p-3 group-hover:bg-slate-900 group-hover:text-white transition-colors duration-200"><MessageCircle size={18} /></span>
              <span>
                <span className="block text-xs uppercase tracking-wider text-slate-500 font-bold">WhatsApp</span>
                <span className="font-semibold text-slate-900">Message us anytime</span>
              </span>
            </a>
            <div className="flex items-center gap-4" data-testid="contact-address">
              <span className="border border-slate-300 p-3"><MapPin size={18} /></span>
              <span>
                <span className="block text-xs uppercase tracking-wider text-slate-500 font-bold">Workshop</span>
                <span className="font-semibold text-slate-900">Unit 19B / 4 Louise Avenue, Ingleburn NSW</span>
              </span>
            </div>
          </div>
        </div>
        <form onSubmit={submit} className="lg:col-span-3 bg-white border border-slate-200 p-8 md:p-10" data-testid="enquiry-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="enquiry-name" className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Name *</Label>
              <Input id="enquiry-name" data-testid="enquiry-name-input" required value={form.name} onChange={set("name")} className="mt-2 rounded-sm" placeholder="Your name" />
            </div>
            <div>
              <Label htmlFor="enquiry-email" className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Email *</Label>
              <Input id="enquiry-email" data-testid="enquiry-email-input" required type="email" value={form.email} onChange={set("email")} className="mt-2 rounded-sm" placeholder="you@email.com" />
            </div>
            <div>
              <Label htmlFor="enquiry-phone" className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Phone</Label>
              <Input id="enquiry-phone" data-testid="enquiry-phone-input" value={form.phone} onChange={set("phone")} className="mt-2 rounded-sm" placeholder="04xx xxx xxx" />
            </div>
            <div>
              <Label htmlFor="enquiry-service" className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Service</Label>
              <select
                id="enquiry-service"
                data-testid="enquiry-service-select"
                value={form.service}
                onChange={set("service")}
                className="mt-2 w-full border border-input bg-background px-3 py-2 text-sm rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="">Select a service…</option>
                {SERVICE_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-6">
            <Label htmlFor="enquiry-message" className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Message *</Label>
            <Textarea
              id="enquiry-message"
              data-testid="enquiry-message-input"
              required
              value={form.message}
              onChange={set("message")}
              rows={5}
              className="mt-2 rounded-sm"
              placeholder="Tell us about your job — measurements, photos of the damage, timelines… anything helps."
            />
          </div>
          <Button
            type="submit"
            disabled={sending}
            data-testid="enquiry-submit-button"
            className="mt-8 w-full sm:w-auto bg-slate-900 hover:bg-sky-500 text-white rounded-sm px-10 py-6 text-base font-medium transition-colors duration-300"
          >
            {sending ? "Sending…" : "Send Enquiry"}
          </Button>
        </form>
        <div className="lg:col-span-5 border border-slate-200 bg-white" data-testid="contact-map">
          <iframe
            title="ASAP Glass & Aluminium — Unit 19B / 4 Louise Avenue, Ingleburn NSW"
            src="https://maps.google.com/maps?q=Unit%2019B%2C%204%20Louise%20Avenue%2C%20Ingleburn%20NSW%202565%2C%20Australia&t=&z=15&ie=UTF8&iwloc=&output=embed"
            className="w-full h-[380px] border-0"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            data-testid="contact-map-iframe"
          />
        </div>
      </div>
    </section>
  );
}
